import { useQuery } from '@apollo/client';
import { createContext, useMemo, useState } from 'react';
import { downloadXlsx } from '../../../../components/Downloads/helpers';
import GraphControls from '../../../../components/Graphs/GraphControls';
import { useGetFeatureFlags } from '../../../../hooks/useGetFeatureFlags';
import { GOOGLE_ANALYTICS_TAG } from '../../constants';
import { GRAPH_ASSETS_QUERY_NEW } from '../operations';
import {
  ASSET_AGGREGATE,
  COMPANY_AGGREGATE,
  ENTITLEMENT_GRAPH_TYPE_OPTION,
  getFilteredAggregateTypes,
  graphTitles,
  REMAINING_RESERVES,
  treeGraphTypeChildren,
  treeMapTypes,
  treemapAggregateTypes,
} from '../utils';

const TreemapContext = createContext();

export const TreemapProvider = ({ children, portfolios }) => {
  const { portfolioEntitlement: entitlementEnabled } = useGetFeatureFlags();
  const [openControlsModal, setOpenControlsModal] = useState(false);

  const [aggregate, setAggregate] = useState({
    'Layer One': COMPANY_AGGREGATE.name,
    'Layer Two': ASSET_AGGREGATE.name,
    'Layer Three': '',
  });
  const [graphType, setGraphType] = useState(REMAINING_RESERVES);
  const [graphTypeOptions, setGraphTypeOptions] = useState([]);
  const [checkboxAggregates, setCheckboxAggregates] = useState({});
  const [zoomedNode, setZoomedNode] = useState('');

  const getFilteredGraphTypeOptions = () => {
    // Get options from treeGraphTypeChildren that are selected
    const treeOptions =
      treeGraphTypeChildren[graphType.value]
        ?.filter(option => graphTypeOptions.some(selected => selected.name === option.name))
        .map(option => option.name) || [];

    // Only include Entitlement if feature flag is enabled and it's selected
    if (entitlementEnabled) {
      const entitlementSelected = graphTypeOptions.some(
        option => option.name === ENTITLEMENT_GRAPH_TYPE_OPTION.name,
      );
      if (entitlementSelected) {
        return [...treeOptions, ENTITLEMENT_GRAPH_TYPE_OPTION.name];
      }
    }

    return treeOptions;
  };

  // TODO: permanently replace old queries with new ones
  const { data, loading } = useQuery(GRAPH_ASSETS_QUERY_NEW, {
    variables: {
      aggregates: Object.values(aggregate).filter(a => a !== ''),
      chartType: 'treemap',
      graphType: graphType.value,
      graphTypeOptions: getFilteredGraphTypeOptions(),
      portfolios,
    },
  });

  const downloadCallback = (errorHandler, successHandler) => {
    const path = 'portfolio-analysis/export-asset-treemap-data/';
    const body = {
      graphType: graphType.value,
      graphTypeOptions: getFilteredGraphTypeOptions(),
      portfolios,
    };
    const downloadName = `${portfolios[0].name}_Asset_${graphType.label}_Data.xlsx`;

    downloadXlsx(path, body, downloadName, errorHandler, successHandler);
  };

  const onClick = ({ points }) => {
    const node = points[0];
    if (node?.id) {
      setZoomedNode(node.id);
    } else {
      setZoomedNode('');
    }
  };

  const title = `${graphType.label} ${graphTitles[graphType.value]}`;

  return (
    <TreemapContext.Provider
      value={{
        title,
        setOpenControlsModal,
        labels: data?.graphAssetsNew?.analytics[0].labels || [],
        parents: data?.graphAssetsNew?.analytics[0].parents || [],
        values: data?.graphAssetsNew?.analytics[0].values || [],
        customdata: data?.graphAssetsNew?.analytics[0].customdata || [],
        ids: data?.graphAssetsNew?.analytics[0].ids || [],
        hovertemplate: data?.graphAssetsNew?.analytics[0].hovertemplate || '',
        texttemplate: data?.graphAssetsNew?.analytics[0].texttemplate || [],
        loading,
        downloadCallback,
        onClick,
        zoomedNode,
        googleAnalyticsTag: GOOGLE_ANALYTICS_TAG,
      }}
    >
      {children}
      <GraphControls
        aggregate={aggregate}
        aggregateTypes={useMemo(
          () => getFilteredAggregateTypes(treemapAggregateTypes, entitlementEnabled),
          [entitlementEnabled],
        )}
        aggregateLayers={Object.keys(aggregate)}
        checkboxAggregates={checkboxAggregates}
        googleAnalyticsTag={GOOGLE_ANALYTICS_TAG}
        graphType={graphType}
        graphTypeChildren={treeGraphTypeChildren}
        graphTypeOptions={graphTypeOptions}
        includeOptions
        open={openControlsModal}
        setAggregate={setAggregate}
        setCheckboxAggregates={setCheckboxAggregates}
        setGraphType={setGraphType}
        setGraphTypeOptions={setGraphTypeOptions}
        setOpen={setOpenControlsModal}
        types={treeMapTypes}
      />
    </TreemapContext.Provider>
  );
};

export const TreemapConsumer = TreemapContext.Consumer;

export default TreemapContext;
